import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RegionHierarchyInput } from '../components/RegionHierarchyInput';
import { useCreateRegion } from '../hooks/useCreateRegion';
import { createRegionSchema, type CreateRegionFormValues } from '../schemas/region-schema';
import { RegionSelector, RegionSelectorLabels } from '../types/regions.types';
import { CountryHierarchyMultiSelect } from '@/features/countries/components/CountryHierarchyMultiSelect';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { FormControl, FormField, FormItem, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface CreateRegionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialParentId?: string | null;
  parentSelector?: number | null;
}

export function CreateRegionModal({
  open,
  onOpenChange,
  initialParentId,
  parentSelector,
}: CreateRegionModalProps) {
  const { createRegion, isCreating } = useCreateRegion();
  const [pendingValues, setPendingValues] = useState<CreateRegionFormValues | null>(null);

  const isCreatingChild = !!initialParentId;

  const form = useForm<CreateRegionFormValues>({
    resolver: zodResolver(createRegionSchema),
    defaultValues: {
      countryIds: [],
      selector: undefined as unknown as RegionSelector,
      name: '',
      parentId: null,
    },
  });

  const selectedCountryIds = form.watch('countryIds');
  const selectedSelector = form.watch('selector');
  const countryIdsKey = selectedCountryIds.join(',');

  useEffect(() => {
    // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
    if (open) {
      const selector = isCreatingChild && parentSelector !== null ? parentSelector : undefined;
      form.reset({
        countryIds: [],
        selector: selector as unknown as RegionSelector,
        name: '',
        parentId: initialParentId || null,
      });
    }
  }, [open, form, initialParentId, parentSelector, isCreatingChild]);

  useEffect(() => {
    form.setValue('parentId', null);
  }, [countryIdsKey, selectedSelector, form]);

  const onSubmit = (values: CreateRegionFormValues) => {
    setPendingValues(values);
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    const payload = {
      countryIds: pendingValues.countryIds,
      name: pendingValues.name,
      selector: pendingValues.selector,
      parentId: pendingValues.parentId ?? initialParentId,
    };
    createRegion(payload, {
      onSuccess: () => {
        Notification.success({ title: 'Región registrada exitosamente' });
        setPendingValues(null);
        form.reset();
        onOpenChange(false);
      },
      onError: () => {
        setPendingValues(null);
      },
    });
  };

  const handleClose = () => {
    if (!isCreating) {
      form.reset();
      onOpenChange(false);
    }
  };

  const getSelectorLabel = (value: number) =>
    RegionSelectorLabels[value as RegionSelector] ?? String(value);

  return (
    <>
      <ModalForm
        open={open}
        headerIcon={<Globe />}
        onOpenChange={handleClose}
        title="Registrar región"
        description="Configura una nueva región en el sistema."
        className="sm:max-w-[425px]"
        data-testid="create-region-modal"
        form={form}
        onSubmit={onSubmit}
        footer={
          <ModalFooter>
            <div className="flex flex-row justify-end w-full gap-4">
              <UnifiedButton
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isCreating}
                data-testid="cancel-create-region-btn"
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                variant="outline"
                actionType="submit"
                disabled={isCreating}
                data-testid="save-region-btn"
              >
                Guardar
              </UnifiedButton>
            </div>
          </ModalFooter>
        }
      >
        <FormField
          control={form.control}
          name="countryIds"
          render={({ field }) => (
            <FormItem data-testid="region-country-select">
              <FormControl>
                <CountryHierarchyMultiSelect
                  label="País / Territorio"
                  placeholder="Seleccionar país"
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isCreating}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="selector"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SelectInput
                  label="Tipo"
                  value={field.value?.toString() || ''}
                  onValueChange={(value) => field.onChange(Number(value))}
                  disabled={isCreatingChild || isCreating}
                  placeholder="Seleccionar tipo"
                  error={form.formState.errors.selector?.message}
                  data-testid="region-selector-select"
                >
                  {Object.entries(RegionSelector).map(([key, value]) => (
                    <SelectItem key={key} value={value.toString()}>
                      {getSelectorLabel(value)}
                    </SelectItem>
                  ))}
                </SelectInput>
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Nombre"
                  placeholder="Ej: Región Norte"
                  data-testid="region-name-input"
                  disabled={isCreating}
                  error={form.formState.errors.name?.message}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {!isCreatingChild && selectedCountryIds.length > 0 && selectedSelector !== undefined && (
          <FormField
            control={form.control}
            name="parentId"
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <RegionHierarchyInput
                    key={`${countryIdsKey}-${selectedSelector}`}
                    countryIds={selectedCountryIds}
                    selector={selectedSelector as RegionSelector}
                    value={value}
                    onSelect={onChange}
                    label="Región padre (opcional)"
                    placeholder="Seleccionar región padre"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </ModalForm>

      <ConfirmDialog
        open={!!pendingValues}
        onOpenChange={(v) => !isCreating && !v && setPendingValues(null)}
        title="Confirmar registro"
        description={
          <span>
            Se registrará la región{' '}
            <span className="font-medium text-light">{pendingValues?.name}</span>.
          </span>
        }
        onConfirm={handleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isCreating}
        onCancel={() => setPendingValues(null)}
      />
    </>
  );
}
